import React, { useState, useEffect, useMemo, useRef } from "react";
import QuestionImage from "./components/QuestionImage";
import { auth, db } from "./firebaseConfig";
import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./CreateQuiz.css";
import { sortQuestionIds } from "./utils/examQuestionSort";
import LearningContentBuilder from "./LearningContentBuilder";
import { normalizeLearningContentBlocks, normalizeLearningContentDraftBlocks } from "./utils/learningContent";
import { saveLearningContentApi } from "./utils/learningContentApi";
import { createQuizApi } from "./utils/quizCreationApi";
import {
  cleanupUnreferencedUploadedMedia,
  uploadLearningContentMediaDrafts,
} from "./utils/learningContentMediaApi";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  BookOpenCheck,
  CheckCircle2,
  ClipboardList,
  ClipboardPen,
  Clock3,
  FileQuestion,
  Filter,
  Library,
  ListChecks,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Trash2,
} from "lucide-react";
import { accountDisplayName, DashboardHeader, DashboardSidebar } from "./Dashboard";
import QuizMcqPreview from "./components/QuizMcqPreview";
import AssessmentOutcomesEditor from "./components/AssessmentOutcomesEditor";
import {
  buildAssessmentOutcomeConfig,
  defaultAssessmentOutcomeConfig,
  normalizeAssessmentOutcomeConfig,
} from "./utils/assessmentOutcomeConfig";
import {
  calculateQuizTotalMarks,
  formatQuizMarkCount,
  quizQuestionValidationMessage,
  validateQuizQuestionSelection,
} from "./utils/quizQuestionCompatibility";

export const deriveQuizSubject = (selectedQuestions) => {
  const subjects = Array.from(
    new Set(
      selectedQuestions
        .map((question) =>
          typeof question?.subject === "string" ? question.subject.trim() : ""
        )
        .filter(Boolean)
    )
  );

  if (subjects.length === 1) return subjects[0];
  if (subjects.length > 1) return "Mixed Subjects";
  return "";
};

const CreateQuiz = ({ user }) => {
  const [selectedSubject, setSelectedSubject] = useState("");
  const [questionBank, setQuestionBank] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [quizTitle, setQuizTitle] = useState("");
  const [quizCode, setQuizCode] = useState("");
  const [defaultTimeLimit, setDefaultTimeLimit] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  // Per-quiz paste permission (teacher-controlled)
  const [allowPaste, setAllowPaste] = useState(false);
  const [filters, setFilters] = useState({
    topic: "",
    level: "",
    specCode: "",
  });
  const [learningContentBlocks, setLearningContentBlocks] = useState([]);
  const [showLearningContentBuilder, setShowLearningContentBuilder] = useState(false);
  const [pendingContentSave, setPendingContentSave] = useState(null);
  const [contentSaveError, setContentSaveError] = useState("");
  const [learningContentMediaDrafts, setLearningContentMediaDrafts] = useState({});
  const mediaDraftsRef = useRef(learningContentMediaDrafts);
  const creationRequestRef = useRef(null);
  const creationDialogRef = useRef(null);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("darkMode") === "true"
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copiedTeacherCode, setCopiedTeacherCode] = useState(false);
  const [questionValidationError, setQuestionValidationError] = useState("");
  const [assessmentOutcomeConfig, setAssessmentOutcomeConfig] = useState(
    defaultAssessmentOutcomeConfig
  );

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    document.documentElement.setAttribute(
      "data-dark-mode",
      darkMode ? "true" : "false"
    );
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "darkMode") setDarkMode(event.newValue === "true");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    mediaDraftsRef.current = learningContentMediaDrafts;
  }, [learningContentMediaDrafts]);

  useEffect(() => () => {
    Object.values(mediaDraftsRef.current).forEach((draft) => {
      if (draft?.previewUrl) URL.revokeObjectURL(draft.previewUrl);
    });
  }, []);

  useEffect(() => {
    if (!isCreating) return undefined;

    const previouslyFocusedElement = document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFrameId = window.requestAnimationFrame(() => {
      creationDialogRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(focusFrameId);
      document.body.style.overflow = previousBodyOverflow;

      if (previouslyFocusedElement instanceof HTMLElement && document.contains(previouslyFocusedElement)) {
        previouslyFocusedElement.focus();
      }
    };
  }, [isCreating]);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDuplicateMode = !!searchParams.get("duplicate");

  // In duplicate mode, default to showing only the pre-selected questions.
  // Initialized lazily so it reads searchParams only once on mount.
  const [showOnlySelected, setShowOnlySelected] = useState(
    () => !!searchParams.get("duplicate")
  );

  const questionById = useMemo(
    () => new Map(questionBank.map((question) => [question.id, question])),
    [questionBank]
  );
  const selectedQuestionIdSet = useMemo(
    () => new Set(selectedQuestionIds),
    [selectedQuestionIds]
  );
  const selectedQuestionDetails = useMemo(
    () => selectedQuestionIds.map((id) => questionById.get(id)).filter(Boolean),
    [selectedQuestionIds, questionById]
  );
  const subjects = useMemo(
    () =>
      Array.from(
        new Set(questionBank.map((question) => question.subject).filter(Boolean))
      ).sort(),
    [questionBank]
  );
  const subjectQuestions = useMemo(
    () => questionBank.filter((question) => question.subject === selectedSubject),
    [questionBank, selectedSubject]
  );
  const filteredQuestions = useMemo(
    () =>
      subjectQuestions.filter((question) => {
        const topicMatch =
          !filters.topic ||
          question.topic?.toLowerCase().includes(filters.topic.toLowerCase());
        const levelMatch =
          !filters.level ||
          question.level?.toLowerCase().includes(filters.level.toLowerCase());
        const specMatch =
          !filters.specCode ||
          question.specCode
            ?.toLowerCase()
            .includes(filters.specCode.toLowerCase());

        return topicMatch && levelMatch && specMatch;
      }),
    [subjectQuestions, filters]
  );
  const displayedQuestions = showOnlySelected
    ? filteredQuestions.filter((question) =>
        selectedQuestionIdSet.has(question.id)
      )
    : filteredQuestions;
  const totalSelectedMarks = calculateQuizTotalMarks(selectedQuestionDetails);
  const quizSubject = deriveQuizSubject(selectedQuestionDetails);

  useEffect(() => {
    setQuestionValidationError("");
  }, [selectedQuestionIds]);

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const scrollToPageTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.scrollingElement?.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
    };

    scrollToPageTop();
    const animationFrameId = window.requestAnimationFrame(scrollToPageTop);
    const timeoutId = window.setTimeout(scrollToPageTop, 0);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(timeoutId);
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = previousScrollRestoration;
      }
    };
  }, []);

  // Load duplicate quiz if duplicate parameter is present
  useEffect(() => {
    const duplicateQuizId = searchParams.get("duplicate");
    if (duplicateQuizId) {
      const loadDuplicateQuiz = async () => {
        try {
          console.log("Loading duplicate quiz:", duplicateQuizId);
          // Fetch the quiz to duplicate
          const quizDoc = await getDoc(doc(db, "quizzes", duplicateQuizId));

          if (quizDoc.exists()) {
            const quizData = quizDoc.data();
            console.log("Original quiz document:", quizData);

            const questionIds = quizData.questionIds || [];
            console.log("Question IDs found:", questionIds.length);

            // Pre-populate form fields
            if (quizData.subject && quizData.subject !== "Mixed Subjects") {
              setSelectedSubject(quizData.subject);
            }
            setDefaultTimeLimit(Math.floor(quizData.defaultTimeLimit / 60).toString()); // Convert seconds to minutes
            setSelectedQuestionIds(questionIds);
            // Preserve the original quiz's paste setting (teacher can still change it)
            setAllowPaste(quizData.allowPaste === true);
            setAssessmentOutcomeConfig(
              normalizeAssessmentOutcomeConfig(quizData.assessmentOutcomeConfig)
            );

            // Leave quiz code empty so teacher must enter a new one
            setQuizCode("");
          } else {
            alert("Test not found. Please try again.");
            navigate("/view-quizzes");
          }
        } catch (error) {
          console.error("Error loading quiz for duplication:", error);
          alert("Failed to load test for duplication. Please try again.");
          navigate("/view-quizzes");
        }
      };

      loadDuplicateQuiz();
    }
  }, [searchParams, navigate]);

  // Retain the full question bank visible to this user. Subject and filter controls
  // derive browse-only views from this collection and never own selection state.
  useEffect(() => {
    const init = async () => {
      if (!user?.uid) return;

      // App already provides the authenticated Firestore profile to every page.
      const userRole = user.role || "teacher";
      // Fetch the full bank visible to this user once.
      try {
        let querySnapshot;
        if (userRole === "admin") {
          querySnapshot = await getDocs(collection(db, "questions"));
        } else {
          querySnapshot = await getDocs(
            query(collection(db, "questions"), where("createdBy", "==", user.uid))
          );
        }
        setQuestionBank(
          querySnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
        );
      } catch (error) {
        console.error("Error fetching questions:", error.message);
      }
    };

    init();
  }, [user?.role, user?.uid]);

  // A mixed-subject duplicate has no browseable subject of its own. Once the bank
  // is available, open the first selected question's real subject.
  useEffect(() => {
    if (!isDuplicateMode || selectedSubject || selectedQuestionDetails.length === 0) {
      return;
    }

    const firstSelectedSubject = selectedQuestionDetails.find(
      (question) => question.subject
    )?.subject;
    if (firstSelectedSubject) setSelectedSubject(firstSelectedSubject);
  }, [isDuplicateMode, selectedSubject, selectedQuestionDetails]);

  // Handle subject selection
  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    // Reset filters when changing subject
    setFilters({ topic: "", level: "", specCode: "" });
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((previous) => ({ ...previous, [name]: value }));
  };

  // Handle question selection
  const handleQuestionSelect = (id) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(id) ? prev.filter((qid) => qid !== id) : [...prev, id]
    );
  };

  // Move a question up in the order
  const moveQuestionUp = (index) => {
    if (index === 0) return; // Can't move up if already at the top
    
    setSelectedQuestionIds(prev => {
      const newOrder = [...prev];
      // Swap with the previous item
      [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
      return newOrder;
    });
  };

  // Move a question down in the order
  const moveQuestionDown = (index) => {
    if (index === selectedQuestionIds.length - 1) return; // Can't move down if already at the bottom
    
    setSelectedQuestionIds(prev => {
      const newOrder = [...prev];
      // Swap with the next item
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      return newOrder;
    });
  };

  // Reorder all selected questions into canonical exam-style order
  const handleReorderQuestions = () => {
    if (selectedQuestionIds.length <= 1) return;
    const sorted = sortQuestionIds(selectedQuestionIds, questionBank);
    setSelectedQuestionIds(sorted);
  };

  // Generate a random quiz code
  const generateRandomCode = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setQuizCode(result);
  };

  // Handle quiz creation
  const handleCreateQuiz = async () => {
    if (pendingContentSave) {
      alert("This test has already been created. Retry the learning-content save or continue without it.");
      return;
    }
    if (!quizCode) {
      alert("Please enter a Test Code (short student join code)!");
      return;
    }
    if (selectedQuestionIds.length === 0) {
      alert("Please select at least one question!");
      return;
    }
    if (!defaultTimeLimit) {
      alert("Please enter a default time limit (in minutes)!");
      return;
    }

    const timeLimitInSeconds = parseInt(defaultTimeLimit, 10) * 60;
    if (isNaN(timeLimitInSeconds) || timeLimitInSeconds <= 0) {
      alert("Please enter a valid time limit in minutes!");
      return;
    }

    if (selectedQuestionDetails.length !== selectedQuestionIds.length) {
      setQuestionValidationError(
        "One or more selected questions could not be loaded. Remove them or refresh the Question Bank before creating this test."
      );
      return;
    }

    const questionValidation = validateQuizQuestionSelection(selectedQuestionDetails);
    if (!questionValidation.isValid) {
      setQuestionValidationError(quizQuestionValidationMessage(questionValidation));
      return;
    }
    setQuestionValidationError("");

    let cleanAssessmentOutcomeConfig;
    try {
      cleanAssessmentOutcomeConfig = buildAssessmentOutcomeConfig(assessmentOutcomeConfig);
    } catch (error) {
      alert(`Please fix Assessment Outcomes before creating this test: ${error.message}`);
      return;
    }

    let normalizedLearningContent = [];
    if (learningContentBlocks.length > 0) {
      try {
        normalizedLearningContent = normalizeLearningContentDraftBlocks(learningContentBlocks, learningContentMediaDrafts);
        setLearningContentBlocks(normalizedLearningContent);
      } catch (error) {
        alert(`Please fix the learning content before creating this test: ${error.message}`);
        setShowLearningContentBuilder(true);
        return;
      }
    }

    setIsCreating(true); // Start loading state

    try {
      console.log(`[QUIZ CREATE] Creating quiz for teacher UID: ${user.uid}`);
      console.log(`[QUIZ CREATE] Requested quiz code: ${quizCode}`);

      const newQuiz = {
        quizCode,
        quizTitle: quizTitle.trim() || quizCode.trim(), // human-readable title; falls back to code
        questionIds: [...selectedQuestionIds],
        defaultTimeLimit: timeLimitInSeconds,
        subject: quizSubject,
        allowPaste: allowPaste,
        assessmentOutcomeConfig: cleanAssessmentOutcomeConfig,
      };
      console.log('[QUIZ CREATE] Title:', newQuiz.quizTitle);
      console.log('[QUIZ CREATE] Short code:', newQuiz.quizCode);

      // The backend assigns the quiz ID and reserves the exact student code.
      const created = await createQuizApi(isDuplicateMode ? "duplicate" : "manual", {
        quizCode: newQuiz.quizCode, title: newQuiz.quizTitle,
        questionIds: newQuiz.questionIds, defaultTimeLimit: newQuiz.defaultTimeLimit,
        subject: newQuiz.subject, allowPaste: newQuiz.allowPaste,
        assessmentOutcomeConfig: newQuiz.assessmentOutcomeConfig,
      }, creationRequestRef);
      const docRef = { id: created.quizId };

      console.log(`[QUIZ CREATE] Quiz created successfully: ${docRef.id}`);

      const successState = {
        quizCode: created.quizCode,
        quizTitle: newQuiz.quizTitle,
        selectedQuestions: selectedQuestionDetails,
        quizSubject: newQuiz.subject,
      };

      if (normalizedLearningContent.length > 0) {
        try {
          const uploadResult = await uploadLearningContentMediaDrafts({
            quizId: docRef.id,
            blocks: normalizedLearningContent,
            mediaDrafts: learningContentMediaDrafts,
            onProgress: (nextBlocks, nextDrafts) => {
              setLearningContentBlocks(nextBlocks);
              setLearningContentMediaDrafts(nextDrafts);
            },
          });
          normalizedLearningContent = normalizeLearningContentBlocks(uploadResult.blocks);
          setLearningContentBlocks(normalizedLearningContent);
          setLearningContentMediaDrafts(uploadResult.mediaDrafts);
          await saveLearningContentApi(docRef.id, normalizedLearningContent);
          await cleanupUnreferencedUploadedMedia(docRef.id, normalizedLearningContent, uploadResult.mediaDrafts);
        } catch (contentError) {
          if (contentError.partialBlocks) setLearningContentBlocks(contentError.partialBlocks);
          if (contentError.partialMediaDrafts) setLearningContentMediaDrafts(contentError.partialMediaDrafts);
          setPendingContentSave({ quizId: docRef.id, successState });
          setContentSaveError(
            contentError.message || "Learning content could not be saved."
          );
          return;
        }
      }

      navigate("/quiz-success", { state: successState });

      // Clear state
      setQuizTitle("");
      setQuizCode("");
      setSelectedQuestionIds([]);
      setDefaultTimeLimit("");
      setLearningContentBlocks([]);
      setLearningContentMediaDrafts({});
      setAssessmentOutcomeConfig(defaultAssessmentOutcomeConfig());
    } catch (error) {
      console.error("Error creating quiz: ", error.message);
      alert(error.code === "code-collision"
        ? "This test code is already used or retired. Please choose a different code."
        : error.message || "Failed to create test. Please try again.");
    } finally {
      setIsCreating(false); // End loading state
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({ topic: "", level: "", specCode: "" });
  };

  const retryContentSave = async () => {
    if (!pendingContentSave || isCreating) return;
    setIsCreating(true);
    setContentSaveError("");
    try {
      const local = normalizeLearningContentDraftBlocks(learningContentBlocks, learningContentMediaDrafts);
      const uploadResult = await uploadLearningContentMediaDrafts({
        quizId: pendingContentSave.quizId,
        blocks: local,
        mediaDrafts: learningContentMediaDrafts,
        onProgress: (nextBlocks, nextDrafts) => {
          setLearningContentBlocks(nextBlocks);
          setLearningContentMediaDrafts(nextDrafts);
        },
      });
      const normalized = normalizeLearningContentBlocks(uploadResult.blocks);
      setLearningContentBlocks(normalized);
      setLearningContentMediaDrafts(uploadResult.mediaDrafts);
      await saveLearningContentApi(pendingContentSave.quizId, normalized);
      await cleanupUnreferencedUploadedMedia(pendingContentSave.quizId, normalized, uploadResult.mediaDrafts);
      navigate("/quiz-success", { state: pendingContentSave.successState });
    } catch (error) {
      if (error.partialBlocks) setLearningContentBlocks(error.partialBlocks);
      if (error.partialMediaDrafts) setLearningContentMediaDrafts(error.partialMediaDrafts);
      setContentSaveError(error.message || "Learning content could not be saved.");
    } finally {
      setIsCreating(false);
    }
  };

  const continueWithoutLearningContent = async () => {
    if (!pendingContentSave) return;
    setIsCreating(true);
    await cleanupUnreferencedUploadedMedia(
      pendingContentSave.quizId,
      [],
      learningContentMediaDrafts
    );
    navigate("/quiz-success", { state: pendingContentSave.successState });
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  const handleCopyTeacherCode = async () => {
    if (!user?.teacherCode) return;
    try {
      await navigator.clipboard.writeText(user.teacherCode);
      setCopiedTeacherCode(true);
      window.setTimeout(() => setCopiedTeacherCode(false), 1800);
    } catch (error) {
      console.error("Could not copy teacher code", error);
    }
  };

  const role = user?.role === "admin" ? "admin" : "teacher";
  const username = accountDisplayName(user, "StudyDen teacher");

  // ── Duplicate-mode view helpers ──────────────────────────────
  // displayedQuestions: derived — no array copy, just a filter view.
  // In "selected only" mode: only questions that are currently selected.
  // In "full bank" mode:     all questions that pass the active filters.
  // Filters always operate on the full filteredQuestions set regardless.
  // Sum maxMark for every selected question that's loaded in the current question set
  if (showLearningContentBuilder) {
    return (
      <div className="learning-content-builder-page">
        <LearningContentBuilder
          blocks={learningContentBlocks}
          onChange={setLearningContentBlocks}
          mediaDrafts={learningContentMediaDrafts}
          onMediaDraftsChange={setLearningContentMediaDrafts}
          onBack={() => setShowLearningContentBuilder(false)}
          onSave={(normalized) => {
            setLearningContentBlocks(normalized);
            setShowLearningContentBuilder(false);
          }}
          saveError={contentSaveError}
        />
      </div>
    );
  }

  return (
    <>
      <div
        className={"sd-dashboard sd-brand-theme sd-role-" + role + " cq-page" + (darkMode ? " sd-dark" : "")}
        inert={isCreating}
        aria-busy={isCreating}
      >
        <DashboardSidebar
          role={role}
          username={username}
          officialDashboardBrand
          creditsRemaining={user?.creditsRemaining ?? null}
          teacherCode={user?.teacherCode || ""}
          onCopyCode={handleCopyTeacherCode}
          copied={copiedTeacherCode}
          onLogout={handleLogout}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          activePath="/create-quiz"
        />

        <div className="sd-workspace cq-workspace">
          <DashboardHeader
            username={username}
            role={role}
            darkMode={darkMode}
            onToggleTheme={() => setDarkMode((value) => !value)}
            onOpenMenu={() => setSidebarOpen(true)}
            search={filters.topic}
            onSearch={(value) =>
              setFilters((previous) => ({ ...previous, topic: value }))
            }
          />

          <main className="sd-main create-quiz-main">
            <section className="cq-hero">
              <div className="cq-hero-copy">
                <button type="button" className="cq-hero-back" onClick={() => navigate("/dashboard")}>
                  <ArrowLeft size={15} />Dashboard
                </button>
                <p className="cq-eyebrow">Create assessment</p>
                <h1>{isDuplicateMode ? "Duplicate Test" : "Create Test"}</h1>
                <p>
                  Build your assessment, configure its settings, and add learning content before publishing.
                </p>
                <div className="cq-step-strip" aria-label="Test creation sections">
                  <span><strong>1</strong>Details</span>
                  <span><strong>2</strong>Settings</span>
                  <span><strong>3</strong>Outcomes</span>
                  <span><strong>4</strong>Questions</span>
                  <span><strong>5</strong>Learning</span>
                  <span><strong>6</strong>Review</span>
                </div>
              </div>
              <div className="cq-hero-art" aria-hidden="true">
                <span className="cq-hero-icon"><ClipboardPen size={40} /></span>
                <i className="cq-orbit cq-orbit-one"><Sparkles size={18} /></i>
                <i className="cq-orbit cq-orbit-two"><FileQuestion size={18} /></i>
                <i className="cq-orbit cq-orbit-three"><CheckCircle2 size={18} /></i>
              </div>
            </section>

            {pendingContentSave && (
              <section className="content-save-recovery" role="alert">
                <span className="cq-recovery-icon"><RefreshCw size={21} /></span>
                <div className="cq-recovery-copy">
                  <h2>Test created, but learning content was not saved</h2>
                  <p>{contentSaveError}</p>
                  <p>Your learning-content draft is still here. Retrying will not create another test.</p>
                </div>
                <div className="content-save-recovery-actions">
                  <button type="button" onClick={retryContentSave} disabled={isCreating}>
                    {isCreating ? "Retrying…" : "Retry Content Save"}
                  </button>
                  <button type="button" onClick={() => setShowLearningContentBuilder(true)} disabled={isCreating}>
                    Edit Draft
                  </button>
                  <button type="button" onClick={continueWithoutLearningContent} disabled={isCreating}>
                    Continue Without Learning Content
                  </button>
                </div>
              </section>
            )}

            <div className="cq-workflow-layout">
              <div className="cq-workflow-main">
                <div className="cq-basics-grid">
                  <section className="quiz-settings-card card cq-section-card" aria-labelledby="cq-details-title">
                    <div className="cq-section-heading">
                      <span className="cq-section-icon cq-tone-blue"><ClipboardList size={20} /></span>
                      <div>
                        <p className="cq-section-kicker">Step 1</p>
                        <h2 id="cq-details-title">Test Details</h2>
                        <p>Give teachers and students a clear way to identify this assessment.</p>
                      </div>
                    </div>
                    <div className="cq-field-stack">
                      <div className="form-group">
                        <label htmlFor="quizTitle">Test Title <span className="quiz-label-note">(displayed to teachers)</span></label>
                        <input
                          id="quizTitle"
                          type="text"
                          placeholder="e.g. Pearson GCSE Computer Science Paper 2 (2024)"
                          value={quizTitle}
                          onChange={(e) => setQuizTitle(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="quizCode">Student Join Code <span className="quiz-label-note">(students type this to join)</span></label>
                        <div className="input-with-button">
                          <input
                            id="quizCode"
                            type="text"
                            placeholder="e.g. P2CS2024"
                            value={quizCode}
                            onChange={(e) => setQuizCode(e.target.value)}
                          />
                          <button type="button" className="generate-code-button" onClick={generateRandomCode} title="Generate random code">
                            <Sparkles size={15} />Generate
                          </button>
                        </div>
                        <p className="quiz-field-note">Short code used for student joining and internal references.</p>
                      </div>
                    </div>
                  </section>

                  <section className="card cq-section-card cq-settings-panel" aria-labelledby="cq-settings-title">
                    <div className="cq-section-heading">
                      <span className="cq-section-icon cq-tone-purple"><Clock3 size={20} /></span>
                      <div>
                        <p className="cq-section-kicker">Step 2</p>
                        <h2 id="cq-settings-title">Assessment Settings</h2>
                        <p>Configure the controls already supported by this test.</p>
                      </div>
                    </div>
                    <div className="cq-field-stack">
                      <div className="form-group">
                        <label htmlFor="timeLimit">Time Limit (minutes)</label>
                        <div className="cq-input-with-icon">
                          <Clock3 size={16} />
                          <input
                            id="timeLimit"
                            type="number"
                            placeholder="Enter time in minutes"
                            value={defaultTimeLimit}
                            onChange={(e) => setDefaultTimeLimit(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="form-group allow-paste-group">
                        <label className="allow-paste-label">
                          <input
                            type="checkbox"
                            checked={allowPaste}
                            onChange={(e) => setAllowPaste(e.target.checked)}
                            className="allow-paste-checkbox"
                          />
                          <span className="cq-checkbox-visual" aria-hidden="true"><CheckCircle2 size={14} /></span>
                          <span>
                            <strong>Allow students to paste</strong>
                            <small>Useful for programming tests where students test code in an IDE first.</small>
                          </span>
                        </label>
                      </div>
                    </div>
                  </section>
                </div>

                <AssessmentOutcomesEditor
                  value={assessmentOutcomeConfig}
                  onChange={setAssessmentOutcomeConfig}
                  teacherUid={user?.uid}
                  kicker="Step 3 · Grading & reattempts"
                />

                <section className="cq-questions-workflow" aria-labelledby="cq-questions-title">
                  <div className="cq-section-heading cq-section-heading-main">
                    <span className="cq-section-icon cq-tone-teal"><FileQuestion size={21} /></span>
                    <div>
                      <p className="cq-section-kicker">Step 4</p>
                      <h2 id="cq-questions-title">Questions</h2>
                      <p>Choose questions from your bank, then arrange the order students will see.</p>
                    </div>
                    <span className="cq-section-stat">
                      {selectedQuestionIds.length} selected · {formatQuizMarkCount(totalSelectedMarks)}
                    </span>
                  </div>

                  <section className="subject-selection-card card">
                    <div className="cq-subheading">
                      <div><span>1</span><div><h3>Select a subject</h3><p>Your selection is preserved when you switch subjects.</p></div></div>
                      <span className="cq-bank-count">{questionBank.length} bank questions</span>
                    </div>
                    {subjects.length > 0 ? (
                      <div className="subject-buttons">
                        {subjects.map((subject) => (
                          <button
                            type="button"
                            key={subject}
                            onClick={() => handleSubjectSelect(subject)}
                            className={selectedSubject === subject ? "subject-button selected" : "subject-button"}
                          >
                            {subject}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="cq-inline-empty">No question-bank subjects are available yet.</div>
                    )}
                  </section>

                  {selectedSubject && (
                    <section className="filters-card card">
                      <div className="cq-subheading">
                        <div><span>2</span><div><h3>Filter the question bank</h3><p>Narrow the visible questions without changing your selection.</p></div></div>
                        <button type="button" className="clear-filters-button" onClick={clearFilters}>
                          <RefreshCw size={14} />Clear Filters
                        </button>
                      </div>
                      <div className="filter-inputs">
                        <div className="form-group">
                          <label htmlFor="topic">Topic</label>
                          <div className="cq-input-with-icon"><Search size={15} />
                            <input id="topic" type="text" name="topic" placeholder="Filter by topic" value={filters.topic} onChange={handleFilterChange} />
                          </div>
                        </div>
                        <div className="form-group">
                          <label htmlFor="level">Level</label>
                          <div className="cq-input-with-icon"><Filter size={15} />
                            <input id="level" type="text" name="level" placeholder="Filter by level" value={filters.level} onChange={handleFilterChange} />
                          </div>
                        </div>
                        <div className="form-group">
                          <label htmlFor="specCode">Spec Code</label>
                          <div className="cq-input-with-icon"><ShieldCheck size={15} />
                            <input id="specCode" type="text" name="specCode" placeholder="Filter by spec code" value={filters.specCode} onChange={handleFilterChange} />
                          </div>
                        </div>
                      </div>
                    </section>
                  )}

                  {selectedSubject && (
                    <section className="questions-table-card card">
                      <div className="questions-header">
                        <div className="cq-subheading">
                          <div>
                            <span>3</span>
                            <div>
                              <h3>{showOnlySelected ? "Selected Questions" : selectedSubject + " Questions"}</h3>
                              <p>{showOnlySelected ? displayedQuestions.length : filteredQuestions.length} question{(showOnlySelected ? displayedQuestions.length : filteredQuestions.length) !== 1 ? "s" : ""} shown</p>
                            </div>
                          </div>
                          {isDuplicateMode && (
                            <button type="button" className="toggle-view-button" onClick={() => setShowOnlySelected((value) => !value)}>
                              {showOnlySelected ? "🔍 View Full Question Bank" : "✅ Showing Selected Questions Only"}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="quiz-action-bar">
                        <span className="quiz-action-summary">
                          {selectedQuestionIds.length === 0
                            ? "No questions selected yet"
                            : selectedQuestionIds.length + " question" + (selectedQuestionIds.length !== 1 ? "s" : "") + " selected" + (totalSelectedMarks > 0 ? " • " + formatQuizMarkCount(totalSelectedMarks) : "")}
                        </span>
                        <button
                          type="button"
                          onClick={handleCreateQuiz}
                          className="quiz-action-create-btn"
                          disabled={selectedQuestionIds.length === 0 || !quizCode || isCreating || Boolean(pendingContentSave)}
                        >
                          <CheckCircle2 size={15} />{isCreating ? "Creating…" : "Create Test"}
                        </button>
                      </div>

                      {questionValidationError && (
                        <div className="cq-question-validation-error" role="alert">
                          <strong>Question needs attention.</strong>
                          <span>{questionValidationError}</span>
                        </div>
                      )}

                      <div className="table-responsive">
                        <table className="questions-table">
                          <thead>
                            <tr>
                              <th>
                                <label className="select-all-container">
                                  <input
                                    type="checkbox"
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        const displayedIds = displayedQuestions.map((question) => question.id);
                                        setSelectedQuestionIds((previous) => {
                                          const newIds = displayedIds.filter((id) => !previous.includes(id));
                                          return [...previous, ...newIds];
                                        });
                                      } else {
                                        const displayedIds = displayedQuestions.map((question) => question.id);
                                        setSelectedQuestionIds((previous) =>
                                          previous.filter((id) => !displayedIds.includes(id))
                                        );
                                      }
                                    }}
                                    checked={
                                      displayedQuestions.length > 0 &&
                                      displayedQuestions.every((question) => selectedQuestionIdSet.has(question.id))
                                    }
                                  />
                                  <span className="select-all-label">Select All</span>
                                </label>
                              </th>
                              <th>Question</th>
                              <th>Spec Code</th>
                              <th>Topic</th>
                              <th>Level</th>
                              <th>Max Mark</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedQuestions.length > 0 ? (
                              displayedQuestions.map((question) => (
                                <tr key={question.id} className={selectedQuestionIdSet.has(question.id) ? "selected" : ""}>
                                  <td data-label="Select">
                                    <input type="checkbox" checked={selectedQuestionIdSet.has(question.id)} onChange={() => handleQuestionSelect(question.id)} />
                                   </td>
                                   <td data-label="Question">
                                     <QuizMcqPreview question={question} />
                                     {question.questionText ? (
                                      <div className="cq-bank-question">{question.questionText}</div>
                                    ) : (question.questionImageURL || question.questionMedia) ? (
                                      <QuestionImage question={question} context={{ type: "question-owner" }}
                                        alt="Question" className="question-image" />
                                    ) : (
                                      <span className="no-question">No Question Provided</span>
                                    )}
                                  </td>
                                  <td data-label="Spec Code">{question.specCode || "-"}</td>
                                  <td data-label="Topic">{question.topic || "-"}</td>
                                  <td data-label="Level">{question.level || "-"}</td>
                                  <td data-label="Max Mark"><span className="cq-marks-pill">{question.maxMark || "-"}</span></td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="no-data">
                                  {subjectQuestions.length === 0
                                    ? "You have no questions for this subject yet."
                                    : showOnlySelected
                                    ? "No questions are currently selected. Click \"View Full Question Bank\" to add questions."
                                    : "No questions match your filter criteria."}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  )}

                  {selectedQuestionIds.length > 0 && (
                    <section className="selected-questions-card card">
                      <div className="question-order-header">
                        <div className="selected-questions-heading">
                          <span className="cq-order-icon"><ListChecks size={19} /></span>
                          <div>
                            <h3>Selected Question Order</h3>
                            <span className="selected-questions-summary">
                              {selectedQuestionIds.length} question{selectedQuestionIds.length !== 1 ? "s" : ""}{ " • " + formatQuizMarkCount(totalSelectedMarks)}
                            </span>
                          </div>
                        </div>
                        <button type="button" className="reorder-questions-btn" onClick={handleReorderQuestions}
                          disabled={selectedQuestionIds.length <= 1}
                          title="Automatically sort questions into exam-style order (1a, 1b, 1c(i), 1c(ii), 2, 2a...)">
                          <RefreshCw size={14} />Reorder Questions
                        </button>
                      </div>
                      <p className="order-instructions">
                        Use the up/down controls for manual adjustments, or reorder automatically into exam order.
                      </p>
                      <div className="selected-questions-list">
                        {selectedQuestionIds.map((questionId, index) => {
                          const question = questionById.get(questionId);
                          if (!question) return null;
                          return (
                            <div key={questionId} className="selected-question-item">
                              <div className="question-order-number">Q{index + 1}</div>
                               <div className="question-content">
                                {question.questionText ? (
                                  <div className="question-text">{question.questionText}</div>
                                ) : (question.questionImageURL || question.questionMedia) ? (
                                  <div className="question-image-container">
                                    <QuestionImage question={question} context={{ type: "question-owner" }}
                                      alt="Question" className="question-thumbnail" />
                                    <span className="image-indicator">[Image Question]</span>
                                  </div>
                                ) : (
                                  <span className="no-question">No Question Provided</span>
                                 )}
                                 <QuizMcqPreview question={question} showOptions />
                                 <div className="question-meta">
                                  <span className="question-subject">{question.subject || "No subject"}</span>
                                  <span className="question-topic">{question.topic || "-"}</span>
                                  <span className="question-spec">{question.specCode || "-"}</span>
                                  <span className="question-marks">
                                    {question.maxMark ? formatQuizMarkCount(question.maxMark) : "-"}
                                  </span>
                                </div>
                              </div>
                              <div className="question-actions">
                                <button type="button" className="move-button up-button" onClick={() => moveQuestionUp(index)}
                                  disabled={index === 0} title="Move question up" aria-label={"Move question " + (index + 1) + " up"}>
                                  <ArrowUp size={15} />
                                </button>
                                <button type="button" className="move-button down-button" onClick={() => moveQuestionDown(index)}
                                  disabled={index === selectedQuestionIds.length - 1} title="Move question down" aria-label={"Move question " + (index + 1) + " down"}>
                                  <ArrowDown size={15} />
                                </button>
                                <button type="button" className="remove-question-button"
                                  onClick={() => setSelectedQuestionIds((previous) => previous.filter((id) => id !== questionId))}>
                                  <Trash2 size={14} />Remove
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  )}
                </section>

                <section className="learning-content-summary-card card cq-section-card" aria-labelledby="cq-learning-title">
                  <div className="cq-learning-icon"><Library size={23} /></div>
                  <div className="cq-learning-copy">
                    <div className="cq-section-heading">
                      <div>
                        <p className="cq-section-kicker">Step 5 · Optional</p>
                        <h2 id="cq-learning-title">Learning Content</h2>
                        <p>Add resources students should review before starting this assessment.</p>
                      </div>
                    </div>
                    <p className="cq-learning-description">
                      Add headings, plain-text guidance, secure external links, images, or PDF resources using the existing content builder.
                    </p>
                    {learningContentBlocks.length > 0 && (
                      <span className="learning-content-added">
                        <CheckCircle2 size={14} />Learning content added · {learningContentBlocks.length} block{learningContentBlocks.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                  <button type="button" className="learning-content-open-button"
                    onClick={() => { setContentSaveError(""); setShowLearningContentBuilder(true); }}>
                    <BookOpenCheck size={16} />{learningContentBlocks.length > 0 ? "Edit Learning Content" : "Add Learning Content"}
                  </button>
                </section>
              </div>

              <aside className="cq-review-card" aria-labelledby="cq-review-title">
                <div className="cq-review-heading">
                  <span className="cq-section-icon cq-tone-green"><CheckCircle2 size={20} /></span>
                  <div><p className="cq-section-kicker">Step 6</p><h2 id="cq-review-title">Review &amp; Create</h2></div>
                </div>
                <p className="cq-review-intro">A live summary of the assessment you are building.</p>
                <dl className="cq-summary-list">
                  <div><dt>Test title</dt><dd>{quizTitle.trim() || "Not set"}</dd></div>
                  <div><dt>Subject</dt><dd>{quizSubject || "Select questions"}</dd></div>
                  <div><dt>Questions</dt><dd>{selectedQuestionIds.length}</dd></div>
                  <div><dt>Total marks</dt><dd>{totalSelectedMarks}</dd></div>
                  <div><dt>Duration</dt><dd>{defaultTimeLimit ? defaultTimeLimit + " min" : "Not set"}</dd></div>
                  <div><dt>Learning resources</dt><dd>{learningContentBlocks.length}</dd></div>
                </dl>
                <div className="cq-readiness">
                  <span className={quizCode && selectedQuestionIds.length > 0 && defaultTimeLimit ? "is-ready" : ""}>
                    <CheckCircle2 size={14} />
                  </span>
                  <p>
                    <strong>{quizCode && selectedQuestionIds.length > 0 && defaultTimeLimit ? "Ready to validate" : "Complete required fields"}</strong>
                    <small>Test code, questions, and duration are required.</small>
                  </p>
                </div>
                <button type="button" onClick={handleCreateQuiz}
                  className="gradient-border-button create-quiz-button"
                  disabled={selectedQuestionIds.length === 0 || isCreating || Boolean(pendingContentSave)}>
                  <ClipboardPen size={16} />{isCreating ? "Creating..." : "Create Test"}
                </button>
                <button type="button" className="cq-review-back" onClick={() => navigate("/dashboard")}>
                  <ArrowLeft size={14} />Back to Dashboard
                </button>
              </aside>
            </div>
          </main>
        </div>
      </div>

      {isCreating && (
        <div className="quiz-creation-processing-overlay">
          <div
            className="quiz-creation-processing-dialog"
            ref={creationDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="quiz-creation-processing-title"
            aria-describedby="quiz-creation-processing-description quiz-creation-processing-help"
            tabIndex="-1"
          >
            <div className="quiz-creation-processing-indicator" aria-hidden="true">
              <span className="quiz-creation-processing-ring"></span>
              <span className="quiz-creation-processing-core"></span>
            </div>
            <h2 id="quiz-creation-processing-title">Creating your test</h2>
            <p className="quiz-creation-processing-description" id="quiz-creation-processing-description"
              role="status" aria-live="polite" aria-atomic="true">
              We’re putting everything together and saving your assessment.
            </p>
            <p className="quiz-creation-processing-help" id="quiz-creation-processing-help">
              Please keep this page open while your test is created.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateQuiz;
